import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useField, useForm } from 'react-final-form';
import { observer } from 'mobx-react';
import { Button } from 'ksnm-common-ui/lib/Button';
import { SelectField } from 'ksnm-common-ui/lib/Form/SelectField';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Tree } from 'ksnm-common-ui/lib/Tree';
import { uniqBy } from 'lodash-es';

import { controlApplicabilityStore } from '@stores/dictionaryStores/controlApplicabilityStore/controlApplicabilityStore';
import { controlsStore } from '@stores/dictionaryStores/controlsStore/controlsStore';
import { controlVersionsStore } from '@stores/dictionaryStores/controlVersionsStore/controlVersionsStore';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { organizationsStore } from '@stores/dictionaryStores/organizationsStore';
import { reportGroupsStore } from '@stores/dictionaryStores/reportGroupsStore/reportGroupsStore';

import styles from './Form.module.scss';

const renderName = (value) => `${value.code} - ${value.name}`;

export const Form = observer(() => {
  const [checkedRows, setCheckedRows] = useState<string[]>([]);

  const { organizations } = organizationsStore;
  const { reportGroups } = reportGroupsStore;
  const { controlVersions } = controlVersionsStore;
  const { controlApplicability } = controlApplicabilityStore;
  const { controls } = controlsStore;

  const controlVersionsWithGroup = useMemo(
    () =>
      controlVersions.map((item) => {
        const repGroupCode = controls.find((control) => control.ctrlCode === item.ctrlCode)?.repGroupCode;

        return { ...item, repGroupCode };
      }),
    [controlVersions, controls],
  );

  const { submit } = useForm();

  useEffect(() => {
    fetchReferences(['organizations', 'reportGroups', 'controlVersions', 'controls']);
  }, []);

  const {
    input: { value: organization },
  } = useField('organization');

  const {
    input: { value: reportGroup },
  } = useField('reportGroup');

  const {
    input: { onChange: changeControlVersions },
  } = useField('controlVersions');

  const onSubmit = useCallback(() => {
    const out = controlVersionsWithGroup.filter((item) => checkedRows.includes(item.ctrlVersCode ?? ''));
    changeControlVersions(out);
    submit();
  }, [submit, changeControlVersions, checkedRows, controlVersionsWithGroup]);

  const onChange = useCallback(() => setCheckedRows([]), []);

  const onCheck = useCallback((checkedKeys, { checkedNodes }) => {
    setCheckedRows(() => checkedKeys.filter((chKey) => !checkedNodes.find((node) => node.key === chKey)?.children));
  }, []);

  const treeData = useMemo(() => {
    const existByOrgAndGroup = controlApplicability
      .filter((control) => control.orgCode === organization && control.repGroupCode === reportGroup)
      .map((control) => control.ctrlVersCode);

    return uniqBy(
      controlVersionsWithGroup.filter(
        (item) => item.repGroupCode === reportGroup && !existByOrgAndGroup.includes(item.ctrlVersCode),
      ),
      'ctrlVersCode',
    ).map((item) => ({
      key: item.ctrlVersCode ?? '',
      title: `${item.ctrlVersCode} - ${item.ctrlVersExt} - ${item.ctrlVersDesc}`,
    }));
  }, [controlApplicability, organization, reportGroup, controlVersionsWithGroup]);

  return (
    <>
      <SelectField
        name="organization"
        items={organizations}
        label="Выбор организации"
        size="small"
        required
        onChange={onChange}
      />
      <SelectField
        name="reportGroup"
        items={reportGroups}
        label="Выбор группы отчетов"
        size="small"
        renderName={renderName}
        required
        onChange={onChange}
      />

      <div className={styles.list}>
        {organization && reportGroup && (
          <Tree
            treeData={treeData}
            search={!!treeData.length}
            showExpandButton={false}
            selectable={false}
            checkable
            onCheck={onCheck}
          />
        )}
      </div>

      <Button
        className={styles.button}
        icon={<Icon name="save" />}
        onClick={onSubmit}
        disabled={checkedRows.length === 0}
        size="large"
      >
        Сохранить
      </Button>
    </>
  );
});
